import React from 'react';

// Utility function for file size formatting
const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// File Preview Modal Component
const FilePreviewModal = ({ file, onDownload, onDismiss, onPrev, onNext, onQuickEdit, onShare }) => {
  const [fileUrl, setFileUrl] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  // Get file type using filename extension or MIME type fallback
  const getFileType = (filename) => {
    const mime = (file && file.mimeType) ? String(file.mimeType).toLowerCase() : '';
    if (mime.startsWith('image/')) return 'image';
    if (mime === 'application/pdf') return 'pdf';
    if (mime.startsWith('video/')) return 'video';
    if (mime.startsWith('audio/')) return 'audio';

    if (!filename) return 'unknown';
    const ext = filename.toLowerCase().split('.').pop();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) return 'image';
    if (['pdf'].includes(ext)) return 'pdf';
    if (['doc', 'docx'].includes(ext)) return 'document';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return 'spreadsheet';
    if (['txt', 'md'].includes(ext)) return 'text';
    if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(ext)) return 'video';
    if (['mp3', 'wav', 'flac', 'm4a', 'aac', 'ogg', 'wma'].includes(ext)) return 'audio';
    return 'other';
  };

  const getFileIcon = (filename) => {
    const ext = filename?.toLowerCase().split('.').pop();
    switch (ext) {
      case 'pdf': return { icon: 'fas fa-file-pdf', color: 'text-red-500' };
      case 'jpg': case 'jpeg': case 'png': case 'gif': case 'webp': case 'bmp': case 'svg': 
        return { icon: 'fas fa-file-image', color: 'text-blue-500' };
      case 'doc': case 'docx': return { icon: 'fas fa-file-word', color: 'text-blue-600' };
      case 'xls': case 'xlsx': return { icon: 'fas fa-file-excel', color: 'text-green-600' };
      case 'csv': return { icon: 'fas fa-file-csv', color: 'text-green-500' };
      case 'txt': case 'md': return { icon: 'fas fa-file-alt', color: 'text-gray-600' };
      case 'mp4': case 'mov': case 'avi': case 'mkv': return { icon: 'fas fa-file-video', color: 'text-purple-500' };
      case 'mp3': case 'wav': case 'flac': case 'm4a': return { icon: 'fas fa-file-audio', color: 'text-yellow-500' };
      default: return { icon: 'fas fa-file', color: 'text-gray-500' };
    }
  };

  const fileType = getFileType(file.filename);
  const fileIcon = getFileIcon(file.filename);

  React.useEffect(() => {
    const fetchUrl = async () => {
      setLoading(true);
      setError(null);
      try {
        // If this is a Google Drive file (has an id but not an S3 key), stream via backend
        if (file && file.source === 'drive' && file.file_key && !file.file_key.includes('/')) {
          const token = localStorage.getItem('access_token');
          const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
          const resp = await fetch(`${apiBase}/integrations/cloud/google/drive/stream?file_id=${encodeURIComponent(file.file_key)}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (!resp.ok) throw new Error(`Failed to stream Drive file: ${resp.status}`);
          // Create blob URL for preview
          const blob = await resp.blob();
          const blobUrl = URL.createObjectURL(blob);
          setFileUrl(blobUrl);
          setLoading(false);
          return;
        }

        const token = localStorage.getItem('access_token');
        const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
        
        const response = await fetch(`${apiBase}/files/${encodeURIComponent(file.file_key)}/download-url`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setFileUrl(data.presigned_url);
        } else {
          throw new Error(`Failed to fetch file URL: ${response.status}`);
        }
      } catch (error) {
        console.error('Failed to fetch file URL:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUrl();
  }, [file.file_key, file.external_url, file.source]);

  // Keyboard navigation and escape to close
  React.useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') {
        onDismiss && onDismiss();
      } else if (e.key === 'ArrowLeft') {
        onPrev && onPrev();
      } else if (e.key === 'ArrowRight') {
        onNext && onNext();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onDismiss, onPrev, onNext]);

  const renderFilePreview = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading file...</p>
          </div>
        </div>
      );
    }

    if (error || !fileUrl) {
      return (
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-24 h-24 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center mx-auto mb-4">
              <i className={`${fileIcon.icon} ${fileIcon.color} text-4xl`}></i>
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {error ? 'Failed to load file' : 'Unable to preview'}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              {error || 'This file type cannot be previewed'}
            </p>
            <button
              onClick={() => onDownload(file)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Download File
            </button>
          </div>
        </div>
      );
    }

    switch (fileType) {
      case 'image':
        return (
          <div className="flex items-center justify-center max-h-[70vh]">
            <img
              src={fileUrl}
              alt={file.filename}
              className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
              onError={() => setError('Failed to load image')}
            />
          </div>
        );

      case 'pdf':
        return (
          <div className="h-[70vh] w-full">
            <iframe
              src={`${fileUrl}#toolbar=1&navpanes=1&scrollbar=1`}
              className="w-full h-full border-0 rounded-lg"
              title={file.filename}
              onError={() => setError('Failed to load PDF')}
            />
          </div>
        );

      case 'text':
        return (
          <div className="h-[70vh] w-full">
            <iframe
              src={fileUrl}
              className="w-full h-full border border-gray-200 dark:border-gray-600 rounded-lg"
              title={file.filename}
              onError={() => setError('Failed to load text file')}
            />
          </div>
        );

      case 'video':
        return (
          <div className="flex items-center justify-center max-h-[70vh]">
            <video
              src={fileUrl}
              controls
              className="max-w-full max-h-full rounded-lg shadow-lg"
              onError={() => setError('Failed to load video')}
            />
          </div>
        );

      case 'audio':
        return (
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="w-24 h-24 bg-yellow-100 dark:bg-yellow-900 rounded-lg flex items-center justify-center mx-auto mb-6">
                <i className="fas fa-music text-yellow-600 dark:text-yellow-400 text-3xl"></i>
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                {file.filename}
              </h3>
              <audio
                src={fileUrl}
                controls
                className="mb-4"
                onError={() => setError('Failed to load audio')}
              />
            </div>
          </div>
        );

      default:
        return (
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="w-24 h-24 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className={`${fileIcon.icon} ${fileIcon.color} text-4xl`}></i>
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Preview not available
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                This file type cannot be previewed in the browser
              </p>
              <button
                onClick={() => onDownload(file)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download File
              </button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={onDismiss}>
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <i className={`${fileIcon.icon} ${fileIcon.color} text-xl`}></i>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate max-w-md">
                  {file.filename}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {formatFileSize(file.size || 0)} • {new Date(file.last_modified || file.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {onQuickEdit && (
                <button
                  onClick={() => onQuickEdit(file)}
                  className="p-2 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-lg transition-colors"
                  title="Quick Edit"
                >
                  <svg className="w-5 h-5 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 20h9" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16.5 3.5a2.121 2.121 0 113 3L7 19l-4 1 1-4 12.5-12.5z" />
                  </svg>
                </button>
              )}
              {onShare && (
                <button
                  onClick={() => onShare(file)}
                  className="p-2 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-lg transition-colors"
                  title="Share"
                >
                  <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 12v.01M12 20a8 8 0 100-16 8 8 0 000 16zm5-9l-6 3m0-6l6 3" />
                  </svg>
                </button>
              )}
              <button
                onClick={() => onDownload(file)}
                className="p-2 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-lg transition-colors"
                title="Download"
              >
                <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </button>
              <button
                onClick={onDismiss}
                className="p-2 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-lg transition-colors"
                title="Close"
              >
                <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-auto max-h-[calc(95vh-100px)] relative">
          {renderFilePreview()}

          {/* Navigation Arrows (for image/video/audio previews primarily) */}
          {onPrev && (
            <button
              type="button"
              onClick={onPrev}
              className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/20 hover:bg-black/30 rounded-full text-white"
              title="Previous"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          {onNext && (
            <button
              type="button"
              onClick={onNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/20 hover:bg-black/30 rounded-full text-white"
              title="Next"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilePreviewModal;












